import React from 'react';
import {
  Container,
  Row,
  Col,
  Hidden,
} from 'react-grid-system';
import fuzzy from 'fuzzy';
import uniqBy from 'lodash.uniqby';
import Radium from 'radium';
import ScrollToComponent from 'react-scroll-to-component';
import { getLocalizedText as text } from '@vp-dst/localization-util';

import BundleUtil from 'utils/BundleUtil';
import NavigationUtil from 'utils/NavigationUtil';
import ColorUtil from 'utils/ColorUtil';
import DSHubModel from 'models/DSHubModel';
import { colors, defaultTextStyle } from 'settings/theme';

import Price from 'components/vistaprintUILibrary/Price';
import Typography from 'components/vistaprintUILibrary/Typography';
import HowItWorks from 'components/HowItWorks';
import ProductSearch from 'components/ProductSearch';
import DesignerSpotlightSwitcher from 'components/DesignerSpotlightSwitcher';
import GetInspired from 'components/GetInspired';
import WhatPeopleAreSaying from 'components/WhatPeopleAreSaying';
import SatisfactionGuaranteed from 'components/SatisfactionGuaranteed';
import Services from 'components/Services';
import AllProductsList from 'components/AllProductsList';

import backgroundTileImage from 'images/ds-bg-tile.png';

import deAngeloProfileImage from 'images/designer-profiles/de-angelo.png';
import sergeProfileImage from 'images/designer-profiles/serge.png';
import syrineProfileImage from 'images/designer-profiles/syrine.png';
import deAngeloWorkSampleImage from 'images/designer-profiles/de-angelo-work-sample.png';
import sergeWorkSampleImage from 'images/designer-profiles/serge-work-sample.png';
import syrineWorkSampleImage from 'images/designer-profiles/syrine-work-sample.png';
import pinIcon from 'images/icons/pin.svg';
import completedIcon from 'images/icons/completed.svg';
import schoolIcon from 'images/icons/school.svg';
import starIcon from 'images/icons/star.svg';
import {
  firePageHit,
  fireSearchBarTextEntered,
  fireSearchBarGoButtonClicked,
  fireSearchResultClicked,
  fireDesignerProfilesDesignerChanged,
  fireProductAndPricingAnchorClicked,
  fireBackToTopAnchorClicked,
  fireProductAndPricingListProductClicked,
  fireSuggestedSearchPillClicked,
} from './utils/AnalyticsUtil';

class App extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      productSearchQuery: '',
      productSearchResults: [],
      selectedDesignerId: 0,
      selectedProduct: null,
    };

    this.topRef = React.createRef();
    this.productSearchRef = React.createRef();
    this.allProductsListRef = React.createRef();

    this.productSearchQueryOnChange = this.productSearchQueryOnChange.bind(this);
    this.productSearchResultOnSelected = this.productSearchResultOnSelected.bind(this);
    this.productSearchGoOnClick = this.productSearchGoOnClick.bind(this);
    this.searchForProducts = this.searchForProducts.bind(this);
    this.suggestedSearchPillOnClick = this.suggestedSearchPillOnClick.bind(this);
    this.productAndPricingListProductOnClick = this.productAndPricingListProductOnClick.bind(this);
    this.selectedDesignerOnChange = this.selectedDesignerOnChange.bind(this);
    this.scrollToAllProductsList = this.scrollToAllProductsList.bind(this);
    this.scrollToTop = this.scrollToTop.bind(this);
  }

  componentDidMount() {
    setTimeout(firePageHit, 1000);

    // todo: try/catching for now until we figure out why this doesn't work on some versions of chrome
    try {
      const headerEls = document.getElementsByClassName('header-and-nav');
      if (headerEls.length > 0) {
        window.scrollTo({ top: headerEls[0].offsetHeight, behavior: 'smooth' });
      }
    } catch (error) {
      console.error(error);
    }
  }

  productSearchQueryOnChange(query) {
    fireSearchBarTextEntered();

    this.setState({
      productSearchQuery: query,
      selectedProduct: null, // clear selected product since the query changed
    }, this.searchForProducts(query));
  }

  suggestedSearchPillOnClick(query) {
    fireSuggestedSearchPillClicked();

    this.productSearchRef.current.focus();

    this.setState({
      productSearchQuery: query,
    }, this.searchForProducts(query));
  }

  searchForProducts(query) {
    if (query === '') {
      this.setState({
        productSearchResults: [],
      });

      return;
    }

    const results = fuzzy.filter(query, DSHubModel.allProducts, {
      pre: '<',
      post: '>',
      extract: product => product.localizedName,
    }).map(result => ({
      product: result.original,
      match: result,
    }));

    const deDuplicatedResults = uniqBy(results, result => result.product.localizedName);

    this.setState({
      productSearchResults: deDuplicatedResults,
    });
  }

  productSearchResultOnSelected(product) {
    fireSearchResultClicked(product.pfid);

    this.setState({
      productSearchQuery: product.localizedName,
      selectedProduct: product,
    });
  }

  productSearchGoOnClick() {
    const {
      selectedProduct,
    } = this.state;

    fireSearchBarGoButtonClicked(selectedProduct.pfid);

    NavigationUtil.navigateTo(selectedProduct.requestURL, {
      xNav: {
        pageZone: 'Search',
        details: selectedProduct.pfid,
      },
    });
  }

  productAndPricingListProductOnClick(product) {
    fireProductAndPricingListProductClicked(product.pfid);

    this.setState({
      productSearchQuery: product.localizedName,
    });

    NavigationUtil.navigateTo(product.requestURL, {
      xNav: {
        pageZone: 'ProductList',
        details: product.pfid,
      },
    });
  }

  selectedDesignerOnChange(designerId, designerName) {
    fireDesignerProfilesDesignerChanged(designerName);

    this.setState({
      selectedDesignerId: designerId,
    });
  }

  scrollToAllProductsList() {
    fireProductAndPricingAnchorClicked();

    ScrollToComponent(this.allProductsListRef.current, {
      offset: -150, // hack to make this scroll to the top of the section; for some reason it scrolls a bit too far
    });
  }

  scrollToTop() {
    fireBackToTopAnchorClicked();

    ScrollToComponent(this.topRef.current, {
      offset: -10000, // hack to actually make this scroll to the top; for some reason it only scrolls halfway up the page
    });
  }

  render() {
    const {
      productSearchQuery,
      selectedProduct,
      productSearchResults,
      selectedDesignerId,
    } = this.state;

    return (
      <div ref={this.topRef}>
        <Container fluid>
          <Row
            style={{
              position: 'relative', // so that rounded div at the bottom can be positioned relative to this container
              background: `
                url(${BundleUtil.getFullyQualifiedURL(backgroundTileImage)}) top left / 80px 80px repeat,
                linear-gradient(146deg, ${colors.blueMid}, ${colors.blueDark}) no-repeat
              `,
              paddingBottom: 50, // reserve space for the curve overlay
              marginBottom: 30,
            }}
          >
            <Col
              xs={12}
              style={{
                overflow: 'visible',
              }}
            >
              <Row>
                <Col xs={12}>
                  <Typography
                    variant="title"
                    text={text('vistaprint-design-services')}
                    style={{
                      textAlign: 'center',
                      color: 'white',

                      marginTop: 40,
                      marginBottom: 10,
                    }}
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <div
                    style={{
                      textAlign: 'center',
                      color: 'white',
                      marginBottom: 30,
                    }}
                  >
                    <Hidden xs>
                      <Typography
                        variant="heading3"
                        style={{
                          color: 'white',
                          display: 'inline',
                        }}
                        text={text('professional-designers')}
                      />
                      <span
                        style={{
                          paddingLeft: 10,
                          paddingRight: 10,
                          fontSize: '1.5em',
                        }}
                      >
                        |
                      </span>
                    </Hidden>
                    <Typography
                      variant="heading3"
                      style={{
                        color: 'white',
                        display: 'inline',
                      }}
                      text={text('24-hour-turnaround')}
                    />
                    <span
                      style={{
                        paddingLeft: 10,
                        paddingRight: 10,
                        fontSize: '1.5em',
                      }}
                    >
                      |
                    </span>
                    <div
                      style={{
                        display: 'inline-block',
                      }}
                    >
                      <Typography
                        variant="heading3"
                        style={{
                          marginRight: 5,
                          color: 'white',
                          display: 'inline',
                        }}
                        text={text('starts-at')}
                      />
                      <Price
                        priceHTML={DSHubModel.productDesignServicePriceHTML}
                        style={{
                          ...defaultTextStyle,
                          color: 'white',
                          fontSize: '1.5em',
                        }}
                      />
                    </div>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={8} lg={6} offset={{ md: 2, lg: 3 }} style={{ overflow: 'visible' }}>
                  <ProductSearch
                    inputRef={this.productSearchRef}
                    placeholderText={text('what-can-we-design')}
                    query={productSearchQuery}
                    results={productSearchResults}
                    queryOnChange={this.productSearchQueryOnChange}
                    onResultSelected={this.productSearchResultOnSelected}
                    goOnClick={this.productSearchGoOnClick}
                    goButtonDisabled={selectedProduct === null}
                    containerStyle={{
                      marginBottom: 15,
                    }}
                  />
                  <div
                    style={{
                      width: '100%',
                      marginTop: 5,
                      display: 'flex',
                      overflowX: 'auto',
                    }}
                  >
                    {DSHubModel.getSearchExamples().map(query => (
                      <button
                        type="button"
                        key={`popular-query-${query.split(' ').join('')}`}
                        style={{
                          border: 'none',

                          ...defaultTextStyle,
                          fontSize: '1.3em',
                          color: 'white',

                          whiteSpace: 'nowrap', // don't wrap text to next line
                          borderRadius: 16,
                          paddingTop: 3, // helps vertically align text (this is bad)
                          paddingRight: 12,
                          paddingLeft: 12,
                          height: 30,
                          margin: 5,
                          backgroundColor: colors.blueBright,
                          transition: 'all 0.2s ease-in-out',
                          cursor: 'pointer',

                          ':hover': {
                            backgroundColor: ColorUtil.rgbaFromHex(colors.blueBright, 0.6),
                          },
                        }}
                        onClick={() => this.suggestedSearchPillOnClick(query)}
                      >
                        {query}
                      </button>
                    ))}
                  </div>
                </Col>
              </Row>
              <Row
                style={{
                  marginTop: 30,
                }}
              >
                <Col>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      padding: 4,
                    }}
                  >
                    <button
                      type="button"
                      style={{
                        backgroundColor: 'unset',
                        borderTop: 'none',
                        borderLeft: 'none',
                        borderBottom: '1px solid white',
                        borderRight: 'none',
                        pointer: 'cursor',
                        zIndex: 1, // place this on top of the curve overlay
                      }}
                      onClick={this.scrollToAllProductsList}
                    >
                      <Typography
                        style={{
                          color: 'white',
                        }}
                        text={text('product-list-and-pricing')}
                      />
                    </button>
                  </div>
                </Col>
              </Row>
            </Col>
            {/* curve overlay */}
            <div
              style={{
                height: 100,
                width: '100%',
                position: 'absolute',
                bottom: 0,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  position: 'relative',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  bottom: -80,
                  borderRadius: '50%',
                  width: '125%', // to change curve increase or decrease this
                  height: 100,
                  background: colors.background,
                }}
              />
            </div>
          </Row>
        </Container>
        <Container fluid xs sm>
          <HowItWorks />
          <Services />
          <DesignerSpotlightSwitcher
            selectedDesignerId={selectedDesignerId}
            selectedDesignerOnChange={this.selectedDesignerOnChange}
            designers={[
              {
                id: 0,
                name: 'De\'Angelo',
                profileImageSrc: BundleUtil.getFullyQualifiedURL(deAngeloProfileImage),
                workSampleImageSrc: BundleUtil.getFullyQualifiedURL(deAngeloWorkSampleImage),
                highlights: [
                  {
                    iconSrc: BundleUtil.getFullyQualifiedURL(pinIcon),
                    text: text('montego-bay-jamaica'),
                  },
                  {
                    iconSrc: BundleUtil.getFullyQualifiedURL(completedIcon),
                    text: text('completed-n-orders-last-year').replace('{{order-count}}', '2385'),
                  },
                  {
                    iconSrc: BundleUtil.getFullyQualifiedURL(schoolIcon),
                    text: text('self-taught'),
                  },
                  {
                    iconSrc: BundleUtil.getFullyQualifiedURL(starIcon),
                    text: text('average-rating-n-out-of-5').replace('{{star-rating}}', '4.0'),
                  },
                ],
                quote: text('what-drives-me-toward-perfection'),
              },
              {
                id: 1,
                name: 'Syrine',
                profileImageSrc: BundleUtil.getFullyQualifiedURL(syrineProfileImage),
                workSampleImageSrc: BundleUtil.getFullyQualifiedURL(syrineWorkSampleImage),
                highlights: [
                  {
                    iconSrc: BundleUtil.getFullyQualifiedURL(pinIcon),
                    text: text('tunis-tunisia'),
                  },
                  {
                    iconSrc: BundleUtil.getFullyQualifiedURL(completedIcon),
                    text: text('completed-n-orders-last-year').replace('{{order-count}}', '945'),
                  },
                  {
                    iconSrc: BundleUtil.getFullyQualifiedURL(schoolIcon),
                    text: text('high-institute-of-multimedia-arts'),
                  },
                  {
                    iconSrc: BundleUtil.getFullyQualifiedURL(starIcon),
                    text: text('average-rating-n-out-of-5').replace('{{star-rating}}', '4.5'),
                  },
                ],
                quote: text('it-started-as-a-hobby'),
              },
              {
                id: 2,
                name: 'Serge',
                profileImageSrc: BundleUtil.getFullyQualifiedURL(sergeProfileImage),
                workSampleImageSrc: BundleUtil.getFullyQualifiedURL(sergeWorkSampleImage),
                highlights: [
                  {
                    iconSrc: BundleUtil.getFullyQualifiedURL(pinIcon),
                    text: text('manilla-philippines'),
                  },
                  {
                    iconSrc: BundleUtil.getFullyQualifiedURL(completedIcon),
                    text: text('completed-n-orders-last-year').replace('{{order-count}}', '578'),
                  },
                  {
                    iconSrc: BundleUtil.getFullyQualifiedURL(schoolIcon),
                    text: text('mapua-institute-of-technology'),
                  },
                  {
                    iconSrc: BundleUtil.getFullyQualifiedURL(starIcon),
                    text: text('average-rating-n-out-of-5').replace('{{star-rating}}', '4.4'),
                  },
                ],
                quote: text('trust-is-what-drives-me'),
              },
            ]}
          />
        </Container>
        <Container fluid>
          <WhatPeopleAreSaying />
        </Container>
        <Container fluid xs sm>
          <GetInspired />
          <SatisfactionGuaranteed />
          <div ref={this.allProductsListRef}>
            <AllProductsList
              productsByPriceMultiplier={DSHubModel.productsByPriceMultiplier}
              productOnClick={this.productAndPricingListProductOnClick}
            />
          </div>
          <Row
            style={{
              marginTop: 30,
            }}
          >
            <Col>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: 4,
                }}
              >
                <button
                  type="button"
                  className="textbutton textbutton-skin-primary"
                  style={{
                    boxSizing: 'border-box',
                    padding: 0,
                    margin: 0,
                    height: 40,
                    width: 40,

                    border: 'none',

                    borderRadius: '50%',
                    cursor: 'pointer',

                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                  onClick={this.scrollToTop}
                >
                  <span className="textbutton-icon textbutton-icon-skin-white textbutton-icon-arrow-u" />
                </button>
              </div>
              <Typography
                text={text('back-to-top')}
                style={{
                  textAlign: 'center',
                }}
              />
            </Col>
          </Row>
        </Container>
      </div>
    );
  }
}

export default Radium(App);



// WEBPACK FOOTER //
// ./src/App.js